import { NotificationsManager } from "@core/managers/notifications.manager";
import { UserStorageManager } from "@core/managers/user-storage.manager";
import { JavascriptHelpers } from "@core/helpers";
import { Logger } from "@core/helpers/logger.helpers";
import { EtabProfileService } from "@core/services";
import { EventManager } from "@core/managers/event.manager";
import api from "@core/plugins/api";
import { i18n } from "@core/plugins/i18n";
import { CORE_EVENTS, ETAB_ID_URL_PARAMETER, USER_BO_ID_URL_PARAMETER, X_AUTH_ETAB_SUPERVISED, X_AUTH_USER_SUPERVISOR } from "@core/constants";
import { SupervisorModel } from "@core/models";

let _supervisorInterceptorIdentifier;
let _supervisorModeStarted = false;
let _supervisedEtabId;

export class SupervisorManager {
  static get hasStarted() {
    return _supervisorModeStarted;
  }

  static set hasStarted(value) {
    _supervisorModeStarted = value;
  }

  static get supervisedEtabId() {
    return _supervisedEtabId;
  }

  static set supervisedEtabId(value) {
    _supervisedEtabId = value;
  }

  static get interceptorIdentifier() {
    return _supervisorInterceptorIdentifier;
  }

  static set interceptorIdentifier(value) {
    _supervisorInterceptorIdentifier = value;
  }

  static async initSupervisorMode() {
    if (!SupervisorManager.hasStarted) {
      const supervisorData = (await UserStorageManager.getSupervisorData()) || {};
      const supervisor = new SupervisorModel(supervisorData);

      if (supervisor.isValid()) {
        Logger.info("Supervisor init - supervisorData detected, start supervisor mode");
        SupervisorManager.startSupervisorMode({ supervisor, shouldSaveData: false });
      }
    }
  }

  static async startSupervisorMode({ supervisor, shouldSaveData } = {}) {
    if (!supervisor || !supervisor.isValid()) {
      return;
    }

    SupervisorManager.hasStarted = true;
    SupervisorManager.supervisedEtabId = supervisor.getEtabId();

    if (shouldSaveData) {
      UserStorageManager.setSupervisorData(supervisor);
    }

    SupervisorManager.useApiInterceptor({ supervisor });
    SupervisorManager.registerOnLogoutEvent();
    await SupervisorManager.notifyUser();
    Logger.info("Supervisor mode started");
    return;
  }

  static stopSupervisorMode() {
    SupervisorManager.hasStarted = false;
    UserStorageManager.removeSupervisorData();
    SupervisorManager.ejectApiInterceptor();
    SupervisorManager.unregisterOnLogoutEvent();
    Logger.info("Supervisor mode stopped");
  }

  static useApiInterceptor({ supervisor }) {
    if (!SupervisorManager.interceptorIdentifier) {
      SupervisorManager.interceptorIdentifier = api.private.interceptors.request.use(SupervisorManager.interceptorBuilder({ supervisor }));
      api.comet.interceptors.request.use(SupervisorManager.interceptorBuilder({ supervisor }));
    }
  }

  static ejectApiInterceptor() {
    if (SupervisorManager.interceptorIdentifier) {
      api.private.interceptors.request.eject(SupervisorManager.interceptorIdentifier);
    }
  }

  static interceptorBuilder({ supervisor }) {
    return (config) => {
      config.headers[X_AUTH_ETAB_SUPERVISED] = supervisor.getEtabId();
      config.headers[X_AUTH_USER_SUPERVISOR] = supervisor.getSupervisorId();

      return config;
    };
  }

  static registerOnLogoutEvent() {
    EventManager.on(CORE_EVENTS.ON_LOGOUT, SupervisorManager.stopSupervisorMode);
  }

  static unregisterOnLogoutEvent() {
    EventManager.off(CORE_EVENTS.ON_LOGOUT, SupervisorManager.stopSupervisorMode);
  }

  static async notifyUser() {
    const etabName = await EtabProfileService.getEtabName();

    NotificationsManager.create({
      message: i18n.global.t("INFO.@SUPERVISOR_MODE_ETAB", { etabName }),
      options: {
        timeout: 0,
        color: "black",
        icon: "warning",
        position: "bottom-left",
      },
    });
  }

  /**
   * Extract idEtab from an url
   * @param {String} url
   * @example
   * https://api-dev.extracadabra.com?id_etab=1337
   * @returns {String}
   */
  static getEtabIdFromUrl({ url }) {
    return JavascriptHelpers.getUrlParameter({ url, parameter: ETAB_ID_URL_PARAMETER });
  }

  /**
   * Extract idUserBo from an url
   * @param {String} url
   * @example
   * https://api-dev.extracadabra.com?id_user_bo=80085
   * @returns {String}
   */
  static getSupervisorIdFromUrl({ url }) {
    return JavascriptHelpers.getUrlParameter({ url, parameter: USER_BO_ID_URL_PARAMETER });
  }
}
